import React from 'react';
import {
  BooleanInput,
  ReferenceArrayInput,
  AutocompleteArrayInput,
  SelectInput,
} from 'react-admin';
import {
  makeStyles,
  Grid,
  Paper,
  Typography,
  Tooltip,
} from '@material-ui/core';
import ReferenceArrayChild from '../../../components/ReferenceArrayChild';
import { getZ3PConfigValue, perPage } from '../../../helper';
import { carMakeOptions } from '../../../VehicleConstants';
import PriceInput from '../../PriceInput';

const useStyles = makeStyles(() => ({
  paperBase: {
    padding: '40px 30px',
    margin: '15px 0',
  },
  w100: {
    width: '100%',
  },
  formRoot: {
    '& .MuiTextField-root': {
      width: '100%',
    },
    '& .MuiFilledInput-root': {
      backgroundColor: '#fdfdfd',
    },
  },
  pLR10: {
    padding: '0 10px',
  },
  center: {
    textAlign: 'center',
  },
}));

const twoColumnProps = {
  item: true,
  xs: 12,
  sm: 6,
};

const TAX_TYPE_CHOICES = [
  {
    id: 'service',
    name: 'Not taxed',
    helpText: 'Tax is not charged on this service',
  },
  {
    id: 'itemized',
    name: 'Itemized',
    helpText: 'Tax is charged on parts only',
  },
  {
    id: 'bundled',
    name: 'Bundled',
    helpText: 'Tax is charged on parts and labor',
  },
];

const DISPLAY_CATEGORY_CHOICES = [
  { id: 'package', name: 'Package' },
  { id: 'standard', name: 'Standard' },
  { id: 'addon', name: 'Addon' },
];

const AdvancedServiceLogicForm = ({
  addOilCostBool,
  extraOilCost,
  setExtraOilCost,
  setDisplayCategory,
}) => {
  const classes = useStyles();
  const isSkippingVehicle = getZ3PConfigValue('is_skipping_vehicle');

  return (
    <Paper className={classes.paperBase}>
      <Typography variant="h5" gutterBottom>
        Advanced Service Logic
      </Typography>
      <Grid container className={classes.formRoot}>
        <Grid item xs={12} className={classes.pLR10}>
          <SelectInput
            resource="service-offering"
            source="tax_type"
            choices={TAX_TYPE_CHOICES}
            initialValue="itemized"
            translateChoice={false}
            optionText={(option) => (
              <>
                <Tooltip title={option.helpText} placement="right">
                  <Typography className={classes.w100}>
                    {option.name}
                  </Typography>
                </Tooltip>
              </>
            )}
          />
        </Grid>

        <Grid item xs={12} className={classes.pLR10}>
          <SelectInput
            resource="service-offering"
            source="display_category"
            choices={DISPLAY_CATEGORY_CHOICES}
            initialValue="standard"
            onChange={(e) => setDisplayCategory(e.target.value)}
          />
        </Grid>
        {!isSkippingVehicle && (
          <Tooltip
            placement="left"
            title="Will only show this service on the booking site to customers with these vehicle makes"
          >
            <Grid item xs={12} className={classes.pLR10}>
              <AutocompleteArrayInput
                resource="service-offering"
                source="offered_vehicle_makes"
                fullWidth
                choices={carMakeOptions}
              />
            </Grid>
          </Tooltip>
        )}
        <Tooltip
          title="This service will not be able to be booked at the same time as incompatible services"
          placement="left"
        >
          <Grid item xs={12} className={classes.pLR10}>
            <ReferenceArrayInput
              resource="service-offering"
              source="incompatible_service_offering_ids"
              reference="service-offering"
              sort={{ field: 'short_name', order: 'ASC' }}
              perPage={perPage}
              className={classes.w100}
            >
              <ReferenceArrayChild
                optionText="short_name"
                source="incompatible_service_offering_ids"
              />
            </ReferenceArrayInput>
          </Grid>
        </Tooltip>
        {!isSkippingVehicle && (
          <Grid {...twoColumnProps} className={classes.pLR10}>
            <BooleanInput
              resource="service-offering"
              source="add_oil_cost"
              initialValue={false}
            />
          </Grid>
        )}
        {!isSkippingVehicle && addOilCostBool && (
          <Grid {...twoColumnProps} className={classes.pLR10}>
            <PriceInput
              resource="price"
              source="extra_oil_cost_per_qt_dollars"
              value={extraOilCost}
              onChange={(e) => setExtraOilCost(e.target.value)}
            />
          </Grid>
        )}
        <Grid item xs={12} className={classes.pLR10}>
          <BooleanInput
            resource="service-offering"
            source="included_with_every_mechanical_service"
            initialValue={false}
          />
        </Grid>
        {!isSkippingVehicle && (
          <Tooltip
            title="Shows “per tire” after pricing and adds 4x of this service to the cart"
            placement="left"
          >
            <Grid item xs={12} className={classes.pLR10}>
              <BooleanInput resource="service-offering" source="per_tire" />
            </Grid>
          </Tooltip>
        )}
      </Grid>
    </Paper>
  );
};

export default AdvancedServiceLogicForm;
